import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Plus, Edit2, Trash2, Calendar, Target, Users, TrendingUp, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency, formatDate } from '@/lib/utils';
import { promotionsApi } from '@/lib/adminApi';
import toast from 'react-hot-toast';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

interface Promotion {
    _id: string;
    title: string;
    description: string;
    type: string;
    status: 'active' | 'scheduled' | 'expired' | 'paused';
    startDate: string;
    endDate: string;
    claimed: number;
    budget: number;
    spent: number;
}

interface Stats {
    activePromotions: number;
    totalClaims: number;
    totalSpent: number;
    avgConversion: number;
}

export default function Promotions() {
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [stats, setStats] = useState<Stats>({
        activePromotions: 0,
        totalClaims: 0,
        totalSpent: 0,
        avgConversion: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [promotionsRes, statsRes] = await Promise.all([
                promotionsApi.getPromotions(),
                promotionsApi.getStats(),
            ]);
            setPromotions(promotionsRes.data.data.promotions);
            setStats(statsRes.data.data);
        } catch (err) {
            console.error('Failed to fetch promotions:', err);
            setError('Failed to load promotions');
            toast.error('Failed to load promotions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (id: string) => {
        toast.success(`Editing promotion #${id}`);
        // TODO: Implement edit modal
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this promotion?')) return;
        
        try {
            await promotionsApi.deletePromotion(id);
            toast.success('Promotion deleted successfully');
            fetchData();
        } catch (err) {
            console.error('Failed to delete promotion:', err);
            toast.error('Failed to delete promotion');
        }
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, 'success' | 'warning' | 'default'> = {
            active: 'success',
            scheduled: 'warning',
            expired: 'default',
            paused: 'default',
        };
        return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <p className="text-red-400">{error}</p>
                <Button onClick={fetchData}>Retry</Button>
            </div>
        );
    }

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Header */}
            <motion.div variants={itemVariants} className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                        <Gift className="w-7 h-7 text-violet-400" />
                        Promotions
                    </h1>
                    <p className="text-slate-400 mt-1">Manage promotional campaigns and offers</p>
                </div>
                <Button variant="default">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Promotion
                </Button>
            </motion.div>

            {/* Stats Grid */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                            <Gift className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.activePromotions}</p>
                            <p className="text-xs text-slate-400">Active Promotions</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.totalClaims.toLocaleString()}</p>
                            <p className="text-xs text-slate-400">Total Claims</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <Target className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{formatCurrency(stats.totalSpent)}</p>
                            <p className="text-xs text-slate-400">Total Spent</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{stats.avgConversion}%</p>
                            <p className="text-xs text-slate-400">Avg Conversion</p>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Promotions List */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4">All Promotions</h3>
                    {promotions.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">
                            <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No promotions found</p>
                            <p className="text-sm mt-1">Create your first promotion to get started</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {promotions.map((promo) => (
                                <div
                                    key={promo._id}
                                    className="p-4 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h4 className="font-semibold text-lg">{promo.title}</h4>
                                                {getStatusBadge(promo.status)}
                                            </div>
                                            <p className="text-sm text-slate-400 mb-3">{promo.description}</p>
                                            <div className="flex items-center gap-4 text-sm text-slate-500">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    <span>{formatDate(promo.startDate)} - {formatDate(promo.endDate)}</span>
                                                </div>
                                                <div className="px-2 py-1 rounded bg-slate-800 text-xs font-medium">
                                                    {promo.type}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(promo._id)}>
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(promo._id)}>
                                                <Trash2 className="w-4 h-4 text-red-400" />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-4 pt-3 border-t border-slate-700">
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Claims</p>
                                            <p className="font-bold">{promo.claimed.toLocaleString()}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Budget</p>
                                            <p className="font-bold">{formatCurrency(promo.budget)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Spent</p>
                                            <div>
                                                <p className="font-bold text-emerald-400">{formatCurrency(promo.spent)}</p>
                                                <div className="mt-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-emerald-500 to-green-400"
                                                        style={{ width: `${promo.budget > 0 ? (promo.spent / promo.budget) * 100 : 0}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </motion.div>
        </motion.div>
    );
}
